(() => {
  if (customElements.get('omega3-anchor-scroll')) return;

  const TARGET_ATTR = 'data-anchor-scroll-target';
  const TRIGGER_ATTR = 'data-anchor-scroll-trigger';

  /* Resolves a merchant-entered value ("#reviews", "reviews", ".my-class") to an element. */
  function findTarget(rawValue) {
    const value = String(rawValue || '').trim();
    if (!value || value === '#') return null;

    const id = value.replace(/^#/, '');
    const byId = document.getElementById(id);
    if (byId) return byId;

    try {
      return document.querySelector(value.charAt(0) === '.' ? value : '#' + id);
    } catch (error) {
      return null;
    }
  }

  /*
   * The pinned box is not always the header element itself — depending on the
   * theme the fixed/sticky rule can sit on a wrapper around it.
   */
  function pinnedHeader() {
    const header =
      document.querySelector('[data-omega3-header]') ||
      document.querySelector('sticky-header');
    if (!header) return null;

    let node = header;
    while (node && node !== document.body) {
      const position = window.getComputedStyle(node).position;
      if (position === 'fixed' || position === 'sticky') return { element: node, position, header };
      node = node.parentElement;
    }
    return null;
  }

  /*
   * Only subtract a header that will actually overlap the target once we get
   * there. A sticky header stops being pinned as soon as its containing block
   * scrolls away, and subtracting it then just leaves a gap above the section.
   */
  function headerOffset(destinationTop) {
    const pinned = pinnedHeader();
    if (!pinned) return 0;

    // The header itself, not the pinned wrapper: an overlay header has a zero-height wrapper.
    const height = pinned.header.getBoundingClientRect().height;
    if (!height) return 0;
    if (pinned.position === 'fixed') return height;

    const container = pinned.element.parentElement;
    if (!container) return 0;

    const containerBottom = container.getBoundingClientRect().bottom + window.pageYOffset;
    return destinationTop + height <= containerBottom ? height : 0;
  }

  function scrollToTarget(target) {
    const targetTop = target.getBoundingClientRect().top + window.pageYOffset;
    const top = targetTop - headerOffset(targetTop);
    window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
  }

  class Omega3AnchorScroll extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.link = this.querySelector('a');
      this.link?.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.link?.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      const target = findTarget(this.link.getAttribute('href'));
      if (!target) return;

      event.preventDefault();
      scrollToTarget(target);
    }
  }

  customElements.define('omega3-anchor-scroll', Omega3AnchorScroll);

  /*
   * Delegated mode: put data-anchor-scroll-target="<section id>" on any existing
   * element to make it scroll to that section on click. Add an optional
   * data-anchor-scroll-trigger="<css selector>" to limit the clickable area to
   * matching descendants — useful when the markup comes from a shared snippet or
   * is injected later by a third-party script.
   */
  function handle(event) {
    const element = event.target instanceof Element ? event.target : null;
    if (!element) return;

    const scope = element.closest('[' + TARGET_ATTR + ']');
    if (!scope) return;

    const triggerSelector = scope.getAttribute(TRIGGER_ATTR);
    if (triggerSelector) {
      let trigger = null;
      try {
        trigger = element.closest(triggerSelector);
      } catch (error) {
        return;
      }
      if (!trigger || !scope.contains(trigger)) return;
    }

    const target = findTarget(scope.getAttribute(TARGET_ATTR));
    if (!target) return;

    // Runs on capture so we win over any handler bound to the trigger itself,
    // but the event keeps bubbling — other listeners (outside-click handlers and
    // the like) must still see it.
    event.preventDefault();
    scrollToTarget(target);
  }

  document.addEventListener('click', handle, true);
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    handle(event);
  });
})();
